import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEO from './SEO';
import './FAQPage.css';
import logo from '../assets/pokeshani-logo.png';

const TermsOfService: React.FC = () => {
  const location = useLocation();
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  
  return (
    <>
      <SEO 
        title="Terms of Service - PokeShani Platform Rules & Guidelines"
        description="PokeShani Terms of Service - Platform rules, creator guidelines, payment terms including zero fees and instant payouts. Legal terms for using our digital products marketplace."
        keywords="terms of service, platform rules, creator guidelines, payment terms, zero fees, instant payouts, legal terms"
        url="https://pokeshani.com/terms-of-service"
      />
      
      <div className="faq-landing-container">
        <div className="faq-header-section">
          <div className="faq-header-content">
            <Link to="/" className="faq-logo-link">
              <img src={logo} alt="PokeShani Logo" className="faq-logo" />
            </Link>
            <h1 className="faq-main-title">Terms of Service</h1>
            <p className="faq-subtitle">Last updated: November 11, 2025</p>
          </div>
        </div>
        
        <div className="faq-main-content">
          <div className="important-notice">
            <h2>⚡ Key Benefits & Terms</h2>
            <p><strong>You get paid within seconds of making any sale with no payout threshold.</strong> PokeShani operates on a zero platform fee model with instant PayPal payouts. By using our platform, you agree to these terms and conditions.</p>
          </div>
          <section className="faq-section">
            <h2>Terms & Conditions</h2>
            
            <div className="faq-item">
              <h3>1. Acceptance of Terms</h3>
              <p>
                By accessing and using PokeShani ("the Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
              </p>
            </div>

            <div className="faq-item">
              <h3>2. Platform Role / Disclaimer</h3>
              <p>
                PokeShani is a software platform that connects buyers and sellers of digital products. <strong>We do not sell products, handle payments, or act as a financial intermediary.</strong> We are not a party to any transaction between buyers and sellers. Our role is limited to providing a platform for sellers to list their digital products and for buyers to discover and purchase them.
              </p>
            </div>

            <div className="faq-item">
              <h3>3. Description of Service</h3>
              <p>
                PokeShani is a platform that enables creators to sell digital products including but not limited to courses, ebooks, software, and other digital content. We provide tools for creating sales pages, processing payments, and managing digital product distribution.
              </p>
            </div>

            <div className="faq-item">
              <h3>4. User Accounts</h3>
              <p>To use our service, you must:</p>
              <ul>
                <li>Provide accurate and complete registration information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Be responsible for all activities under your account</li>
                <li>Notify us immediately of any unauthorized use</li>
              </ul>
            </div>

            <div className="faq-item">
              <h3>5. Creator Responsibilities</h3>
              <p>As a creator using PokeShani, you agree to:</p>
              <ul>
                <li>Own or have proper rights to all content you upload</li>
                <li>Ensure your products comply with applicable laws</li>
                <li>Provide accurate product descriptions and pricing</li>
                <li>Deliver promised digital products to customers</li>
                <li>Handle customer support for your products</li>
              </ul>
            </div>

            <div className="faq-item">
              <h3>6. Payment & Refund Disclaimer</h3>
              <p>
                <strong>All payments are processed directly between buyers and sellers via PayPal.</strong> PokeShani does not hold, process, or transfer funds. Any issues regarding payments, refunds, or disputes must be resolved directly with the seller. <strong>PokeShani is not liable for any financial loss, chargebacks, or payment disputes.</strong>
              </p>
              <p>
                Refund policies are set by individual creators. PokeShani does not guarantee refunds but will assist in resolving disputes between buyers and sellers. Creators are encouraged to have clear refund policies.
              </p>
            </div>

            <div className="faq-item">
              <h3>7. Digital Delivery Clause</h3>
              <p>
                Digital products are delivered automatically upon confirmation of successful payment to the seller. <strong>The platform does not control or verify payments; delivery is triggered solely by our system based on payment confirmation.</strong> We are not responsible for any issues related to product delivery, access, or functionality.
              </p>
            </div>

            <div className="faq-item">
              <h3>8. Seller Responsibility</h3>
              <p>
                Sellers are solely responsible for:
              </p>
              <ul>
                <li>Maintaining valid and legal PayPal accounts</li>
                <li>The products they list and sell</li>
                <li>Ensuring their products comply with all applicable laws and regulations</li>
                <li>Providing accurate product descriptions and delivering promised content</li>
                <li>Handling all customer service and support for their products</li>
              </ul>
              <p>
                <strong>The platform is not responsible for fraud, misuse, or illegal content by sellers.</strong> We do not verify the legitimacy, legality, or accuracy of products sold through our platform.
              </p>
            </div>

            <div className="faq-item">
              <h3>9. User Conduct / Content Policy</h3>
              <p>You may not upload or sell content that:</p>
              <ul>
                <li>Violates any laws or regulations</li>
                <li>Infringes on intellectual property rights</li>
                <li>Contains malware or harmful code</li>
                <li>Is fraudulent, misleading, or deceptive</li>
                <li>Promotes illegal activities</li>
                <li>Contains adult content (unless explicitly permitted)</li>
              </ul>
              <p>
                <strong>PokeShani reserves the right to remove any content or suspend accounts that violate these rules without prior notice.</strong> We may also report illegal activities to the appropriate authorities.
              </p>
            </div>

            <div className="faq-item">
              <h3>10. Prohibited Content</h3>
              <p>You may not upload or sell content that:</p>
              <ul>
                <li>Violates any laws or regulations</li>
                <li>Infringes on intellectual property rights</li>
                <li>Contains malware or harmful code</li>
                <li>Is fraudulent, misleading, or deceptive</li>
                <li>Promotes illegal activities</li>
                <li>Contains adult content (unless explicitly permitted)</li>
              </ul>
            </div>

            <div className="faq-item">
              <h3>19. Legal Responsibility for Content and Transactions</h3>
              <p>
                Sellers are fully and solely responsible for the legality, safety, and compliance of the digital products they upload and sell on PokeShani. By using the platform, sellers agree that:
              </p>
              <p>
                They are the legal publisher of their products.
              </p>
              <p>
                They accept full legal liability for anything they upload, distribute, or sell.
              </p>
              <p>
                They will not list content that is illegal, restricted, harmful, or violates the rights of others.
              </p>
              <p>
                <strong>PokeShani does not review or approve products before they go live.</strong>
              </p>
              <p>
                <strong>We are not responsible for:</strong>
              </p>
              <ul>
                <li>Illegal, harmful, or infringing content uploaded by sellers</li>
                <li>Transactions involving such content</li>
                <li>Any consequences, legal or otherwise, resulting from a seller's actions</li>
              </ul>
              <p>
                If we receive a valid report or become aware of illegal content, we may remove it and suspend the seller's account. We may also cooperate with law enforcement when required.
              </p>
              <p>
                Buyers are responsible for ensuring they are legally allowed to purchase the content they choose.
              </p>
            </div>

            <div className="faq-item">
              <h3>11. Payment Terms</h3>
              <p>
                PokeShani operates on a zero platform fee model for basic accounts. <strong>You get paid within seconds of making any sale with no payout threshold.</strong> Premium features may incur additional charges. Payments are processed through PayPal, and you are responsible for any PayPal fees. We reserve the right to change our fee structure with 30 days notice.
              </p>
              <h4>Key Payment Benefits:</h4>
              <ul>
                <li><strong>Zero Platform Fees:</strong> Keep 100% of your revenue</li>
                <li><strong>Instant Payouts:</strong> Money in your PayPal within seconds</li>
                <li><strong>No Thresholds:</strong> Get paid immediately, regardless of amount</li>
                <li><strong>Direct Payments:</strong> Buyers pay directly to your PayPal account</li>
              </ul>
            </div>

            <div className="faq-item">
              <h3>12. Refunds and Disputes</h3>
              <p>
                Refund policies are set by individual creators. PokeShani does not guarantee refunds but will assist in resolving disputes between buyers and sellers. Creators are encouraged to have clear refund policies.
              </p>
            </div>

            <div className="faq-item">
              <h3>13. Intellectual Property</h3>
              <p>
                You retain ownership of your content. By using PokeShani, you grant us a license to host, display, and distribute your content as necessary to provide our services. The PokeShani platform and branding remain our intellectual property.
              </p>
            </div>

            <div className="faq-item">
              <h3>14. Limitation of Liability</h3>
              <p>
                <strong>To the maximum extent permitted by law, PokeShani shall not be liable for any damages, losses, or disputes arising from transactions between buyers and sellers.</strong>
              </p>
              <p>
                PokeShani provides the platform "as is" without warranties. We are not liable for any indirect, incidental, or consequential damages. Our liability is limited to the amount of fees paid to us in the preceding 12 months.
              </p>
              <p>
                We are not responsible for:
              </p>
              <ul>
                <li>Any financial losses incurred by users</li>
                <li>Disputes between buyers and sellers</li>
                <li>Product delivery issues or failures</li>
                <li>Unauthorized access to user accounts</li>
                <li>Any damages resulting from platform downtime or technical issues</li>
              </ul>
            </div>

            <div className="faq-item">
              <h3>15. Jurisdiction & Law</h3>
              <p>
                <strong>These Terms of Service are made by the authorities of PokeShani.</strong> Any disputes arising out of these terms shall be resolved under the laws of the jurisdiction where PokeShani operates.
              </p>
            </div>

            <div className="faq-item">
              <h3>16. Termination</h3>
              <p>
                We may terminate or suspend accounts that violate these terms. You may terminate your account at any time. Upon termination, you remain responsible for any outstanding obligations.
              </p>
            </div>

            <div className="faq-item">
              <h3>17. Changes to Terms</h3>
              <p>
                We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting. Continued use of the service constitutes acceptance of modified terms.
              </p>
            </div>

            <div className="faq-item">
              <h3>18. Miscellaneous</h3>
              <p>
                <strong>Right to Modify Terms:</strong> PokeShani reserves the right to modify these Terms of Service at any time. We will notify users of significant changes via email or through the platform.
              </p>
              <p>
                <strong>Contact Information:</strong> For questions about these Terms of Service, please contact us at official@pokeshani.com.
              </p>
            </div>
          </section>
        </div>
        
        <div className="faq-footer">
          <div className="faq-footer-content">
            <p>Questions about our terms? <a href="https://x.com/PokeShani" target="_blank" rel="noopener noreferrer" className="contact-link">Contact our legal team</a></p>
            <Link to="/" className="back-home-btn">← Back to Home</Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default TermsOfService;